import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { GeoJSON } from 'ol/format';
import { nanoid } from 'nanoid';
export class BaseCustomFeatures {
    constructor(name, options = {}) {
        this.id = null;
        this.customMap = null;
        this.features = [];
        this.related = [];
        this.listeners = {};
        this.addons = {};
        this._hoveredIn = false;
        this.name = name;
        this.options = {
            ...{
                style: null,
                layer: null,
                geometry: 'Point',
                visibilityListed: true,
            },
            ...options,
        };
        if (typeof options.visibilityElements == 'undefined') {
            this.setOptions({
                visibilityElements: [
                    {
                        name: this.getVisibilityName(),
                        label: options.visibilityLabel ?? this.getVisibilityName(),
                    },
                ],
            });
        }
        if (this.options.layer === null) {
            const source = new VectorSource();
            this.options.layer = new VectorLayer({
                source,
                style: this.options.style,
            });
        }
        this.on('add', ({ feature }) => {
            // temporarily commented out, it caused a problem to JSON.stringify
            // because of circular CustomMap reference
            // feature.set('customFeatures', this);
            // as an alternative
            // @ts-ignore
            feature._mapId = this.customMap?.selector;
        });
    }
    on(type, callback) {
        if (typeof this.listeners[type] == 'undefined') {
            this.listeners[type] = [];
        }
        this.listeners[type].push(callback);
    }
    off(type, callback) {
        if (typeof this.listeners[type] != 'undefined') {
            this.listeners[type] = this.listeners[type].filter((l) => l !== callback);
        }
    }
    get(id) {
        if (typeof id == 'object') {
            return id;
        }
        return this.features.find((f) => f.getId() === id);
    }
    clear() {
        this.features = [];
        if (this.options.layer && this.options.layer.getSource()) {
            this.options.layer.getSource().clear();
        }
        this._trigger('clear');
    }
    remove(ids) {
        if (!Array.isArray(ids)) {
            ids = [ids];
        }
        let features = [
            ...this.features.filter((f) => ids.includes(f.getId())),
            ...this.related.filter((f) => ids.includes(f.get('related_to'))),
        ];
        if (this.options.layer && this.options.layer.getSource()) {
            for (const feature of features) {
                this.options.layer.getSource().removeFeature(feature);
            }
        }
    }
    fromOlFeature(olFeature) {
        let feature = new Feature({
            geometry: olFeature.getGeometry(),
        });
        this._add(feature);
    }
    setOptions(options) {
        this.options = {
            ...this.options,
            ...options,
        };
    }
    fromGeoJSON(geojson) {
        let features = new GeoJSON().readFeatures(geojson, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
        });
        this._add(features);
        return features;
    }
    redraw(id = null) {
        let features = [];
        if (id !== null) {
            features = [
                ...this.features.filter((f) => f.getId() === id),
                ...this.related.filter((f) => f.get('related_to') === id),
            ];
            this.features = this.features.filter((f) => f.getId() !== id);
            this.related = this.related.filter((f) => f.get('related_to') !== id);
        }
        else {
            features = this.features;
        }
        for (let feature of features) {
            if (this.options.layer && this.options.layer.getSource()) {
                this.options.layer.getSource().removeFeature(feature);
            }
        }
        this._add(features.filter((f) => typeof f.get('related_to') == 'undefined'));
    }
    hide() {
        this.options.layer?.setVisible(false);
    }
    show() {
        this.options.layer?.setVisible(true);
    }
    fit(options = {}) {
        options = {
            ...{
                padding: [24, 24, 24, 24],
                maxZoom: undefined,
                feature: undefined,
            },
            ...options,
        };
        if (!this.customMap) {
            return;
        }
        if (!this.options.layer) {
            throw new Error('This features have no layer.');
        }
        let extent = this.options.layer.getSource().getExtent();
        if (typeof options.feature != 'undefined') {
            const feature = this.get(options.feature);
            if (feature && typeof feature.getGeometry() != 'undefined') {
                extent = feature.getGeometry()?.getExtent() ?? null;
            }
        }
        if (!extent || Math.abs(extent[0]) === Infinity) {
            return;
        }
        this.customMap.ol.getView().fit(extent, options);
    }
    addAddon(id, addon) {
        addon._handle(this);
        this.addons[id] = addon;
    }
    removeAddon(id) {
        if (typeof this.addons[id] != 'undefined') {
            this.addons[id]._handleRemove();
            delete this.addons[id];
        }
    }
    getVisibilityName() {
        return this.options.visibilityName ?? this.name;
    }
    getLegendItems() {
        const feature = this.features.length ? this.features[0] : null;
        if (!feature) {
            return [];
        }
        const geometry = feature.getGeometry();
        let style = this.options.style;
        if (typeof style == 'function') {
            style = style(feature, 1);
        }
        if (!geometry || !style) {
            return [];
        }
        return [
            {
                geomType: geometry.getType(),
                style,
                title: this.options.visibilityLabel ?? this.getVisibilityName(),
            },
        ];
    }
    _add(features) {
        if (!Array.isArray(features)) {
            features = [features];
        }
        this.features = [...this.features, ...features];
        if (this.options.layer && this.options.layer.getSource()) {
            this.options.layer.getSource().addFeatures(features);
        }
        for (let feature of features) {
            if (typeof feature.getId() == 'undefined') {
                feature.setId(nanoid());
            }
            this._trigger('add', { feature });
        }
    }
    _addRelated(features, relatedTo, name) {
        if (!Array.isArray(features)) {
            features = [features];
        }
        for (let feature of features) {
            if (typeof feature.getId() == 'undefined') {
                feature.setId(nanoid());
            }
            feature.set('related_to', relatedTo.getId());
            feature.set('name', name);
            // @ts-ignore
            feature._mapId = this.customMap?.selector;
        }
        this.related = [...this.related, ...features];
        if (this.options.layer && this.options.layer.getSource()) {
            this.options.layer.getSource().addFeatures(features);
        }
    }
    _handle(customMap, id) {
        this.customMap = customMap;
        this.id = id;
        if (!this.customMap) {
            throw new Error("Can't handle custom features with no custom map.");
        }
        if (!this.options.layer) {
            throw new Error('There is no layer in custom features.');
        }
        // change visibility if hidden
        if (customMap.hasInterfaceElement('layers-and-base')) {
            const interfaceElement = customMap.interfaceElements['layers-and-base'];
            if (!interfaceElement.isVisible(this.getVisibilityName())) {
                this.hide();
            }
        }
        this.customMap.ol.addLayer(this.options.layer);
        // trigger hover event
        this.customMap.ol.on('pointermove', (e) => {
            let hadFeature = false;
            if (!this.customMap) {
                throw new Error("Can't handle custom features with no custom map.");
            }
            this.customMap.ol.forEachFeatureAtPixel(e.pixel, (feature) => {
                if (this.features.find((f) => f === feature)) {
                    if (!this._hoveredIn) {
                        this._hoveredIn = true;
                        this._trigger('hover-in', { feature });
                    }
                    this._trigger('hover', { feature });
                    hadFeature = true;
                }
            }, {
                layerFilter: (layer) => layer === this.options.layer,
            });
            if (!hadFeature && this._hoveredIn) {
                this._hoveredIn = false;
                this._trigger('hover-out');
            }
        });
        this._trigger('setup');
    }
    _handleRemove() { }
    _trigger(type, data = {}) {
        if (typeof this.listeners[type] != 'undefined') {
            for (let listener of this.listeners[type]) {
                listener(data, () => {
                    this.off(type, listener);
                });
            }
        }
    }
}
